import { Dialog } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  CloudArrowDownIcon,
  TrashIcon,
  XMarkIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';
import type { SyncConflictType } from '../sync/syncDecision';

interface SyncConflictDialogProps {
  isOpen: boolean;
  type: SyncConflictType;
  localBudgetCount: number;
  cloudBudgetCount: number;
  onPushToCloud: () => void;
  onClearLocal: () => void;
  onImportFromCloud: () => void;
  onCancel: () => void;
}

const PRIMARY_BUTTON_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500';
const DANGER_BUTTON_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500';

export function SyncConflictDialog({
  isOpen,
  type,
  localBudgetCount,
  cloudBudgetCount,
  onPushToCloud,
  onClearLocal,
  onImportFromCloud,
  onCancel,
}: SyncConflictDialogProps) {
  const { t } = useTranslation();

  const content = {
    'local-has-data': {
      title: 'syncConflict.title',
      description: 'syncConflict.description',
      question: 'syncConflict.question',
      tip: 'syncConflict.tip',
      actions: [
        { label: 'syncConflict.pushToCloud', icon: CloudArrowUpIcon, onClick: onPushToCloud, className: PRIMARY_BUTTON_CLASS },
        { label: 'syncConflict.clearLocal', icon: TrashIcon, onClick: onClearLocal, className: DANGER_BUTTON_CLASS },
      ],
    },
    'both-have-data': {
      title: 'syncConflict.bothTitle',
      description: 'syncConflict.bothDescription',
      question: 'syncConflict.bothQuestion',
      tip: 'syncConflict.bothTip',
      actions: [
        { label: 'syncConflict.keepCloud', icon: CloudArrowDownIcon, onClick: onImportFromCloud, className: PRIMARY_BUTTON_CLASS },
        { label: 'syncConflict.keepLocal', icon: CloudArrowUpIcon, onClick: onPushToCloud, className: DANGER_BUTTON_CLASS },
      ],
    },
  }[type];

  return (
    <Dialog open={isOpen} onClose={onCancel} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-500" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t(content.title)}
              </Dialog.Title>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                <p className="mb-3">
                  <Trans
                    i18nKey={content.description}
                    values={{
                      count: localBudgetCount,
                      localCount: localBudgetCount,
                      cloudCount: cloudBudgetCount,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className="mb-3">{t(content.question)}</p>
              </div>

              <div className="mt-4 space-y-2">
                {content.actions.map(({ label, icon: Icon, onClick, className }) => (
                  <button key={label} type="button" className={className} onClick={onClick}>
                    <Icon className="h-4 w-4" />
                    {t(label)}
                  </button>
                ))}
                <button
                  type="button"
                  autoFocus
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={onCancel}
                >
                  <XMarkIcon className="h-4 w-4" />
                  {t('syncConflict.cancel')}
                </button>
              </div>

              <p className="mt-4 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <LightBulbIcon className="h-4 w-4 flex-shrink-0" />
                {t(content.tip)}
              </p>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
